import type { PropsWithChildren } from 'react';
import { ConfigProvider } from 'antd';
import { StyleProvider } from '@ant-design/cssinjs';
import { getTheme } from './utils';
import type { ThemeName } from './types';

interface ThemeProviderProps extends PropsWithChildren<{}> {
  themeName?: ThemeName;
}

export const ThemeProvider = ({ children, themeName = 'dark' }: ThemeProviderProps) => {
  const isDark = themeName === 'dark';

  const autofillClass = [
    'autofill:bg-clip-text',
    'autofill:text-lg',
    'autofill:hover:bg-clip-text',
    'autofill:focus:bg-clip-text',
    'autofill:active:bg-clip-text',
    isDark
      ? [
          'autofill:!text-white',
          'autofill:shadow-[inset_0_0_30px_30px_rgb(28,41,52)]',
          'caret-white',
        ].join(' ')
      : [
          'autofill:!text-black',
          'autofill:shadow-[inset_0_0_30px_30px_rgb(255,255,255)]',
          'caret-black',
        ].join(' '),
  ].join(' ');

  // password input is wrapped inside styled span, so it needs a bit adjusted selectors
  const autofillClassInputPassword = [
    '[&>input]:autofill:bg-clip-text',
    '[&>input]:autofill:text-lg',
    '[&>input]:autofill:hover:bg-clip-text',
    '[&>input]:autofill:focus:bg-clip-text',
    '[&>input]:autofill:active:bg-clip-text',
    isDark
      ? [
          '[&>input]:autofill:!text-white',
          '[&>input]:autofill:shadow-[inset_0_0_30px_30px_rgb(28,41,52)]',
          '[&>input]:caret-white',
        ].join(' ')
      : [
          '[&>input]:autofill:!text-black',
          '[&>input]:autofill:shadow-[inset_0_0_30px_30px_rgb(255,255,255)]',
          '[&>input]:caret-black',
        ].join(' '),
  ].join(' ');

  return (
    <div className={isDark ? 'dark' : ''}>
      <StyleProvider layer>
        <ConfigProvider
          theme={getTheme(isDark)}
          input={{
            className: `${autofillClassInputPassword} ${autofillClass}`,
          }}
          inputNumber={{
            className: autofillClass,
          }}
          otp={{
            className: autofillClass,
          }}
          // Render all Antd popups (Popover, Tooltip, Modal, Select dropdown, etc.)
          // into a dedicated container inside this ThemeProvider so Tailwind `dark:`
          // styles work for portal overlays. Falls back to document.body if missing.
          getPopupContainer={() => document.getElementById('popup-portal') ?? document.body}
        >
          {children}
        </ConfigProvider>
        {/* Dedicated portal container for Antd overlays (keeps overlays inside `.dark`) */}
        <div id="popup-portal" />
      </StyleProvider>
    </div>
  );
};
